import {
  useInfiniteQuery,
  useQuery,
  keepPreviousData,
} from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { AGENT_URL } from '../config/env'
import {
  ApiError,
  buildListSearchParams,
  isRecord,
  parsePagination,
} from '../lib/api'
import type { Pagination } from '../lib/api'
import type {
  ClientPhone,
  ClientProviderInfo,
  PatientRecord,
  RawCareGaps,
} from '../features/patients/types'
import type { FilterCondition } from '../utils/filterUtils'

export interface PatientsQueryParams {
  limit?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  // Structured filters serialized as filter[field][operator]=value (parsed +
  // validated server-side by relay's /data/patients).
  filters?: FilterCondition[]
}

export interface PatientsResponse {
  data: PatientRecord[]
  pagination: Pagination
}

interface PatientsApiResponse {
  data: Record<string, unknown>[]
  pagination?: Partial<Pagination>
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null
}

/**
 * Read relay's `phones` array — every number on the record.
 *
 * `phone` stays the flattened first number that the table column and the phone
 * filter match on; `phones` carries the rest, which roughly one client in
 * fourteen has. A relay build that predates the field sends nothing here, and
 * the details sheet falls back to the flat string (see `clientPhones`).
 */
function mapPhones(value: unknown): ClientPhone[] {
  if (!Array.isArray(value)) return []
  const phones: ClientPhone[] = []
  for (const entry of value) {
    if (!isRecord(entry)) continue
    const number = asString(entry.phone_number)
    if (!number) continue
    phones.push({
      phone_number: number,
      label: asString(entry.label),
      status: asString(entry.status),
    })
  }
  return phones
}

/**
 * `provider_info` — the client's assigned provider, distinct from the providers
 * derived from visit history. Consumed by the sheet's care team section, which
 * lands with the per-client visit history that supplies the rest of the team.
 */
function mapProviderInfo(value: unknown): ClientProviderInfo | null {
  if (!isRecord(value)) return null
  const info: ClientProviderInfo = {
    name: asString(value.name),
    phone: asString(value.phone),
    rendering_provider_name: asString(value.rendering_provider_name),
    referring_provider_name: asString(value.referring_provider_name),
  }
  return Object.values(info).some((field) => field !== null) ? info : null
}

/**
 * `care_gaps` passes through unshaped — the column is polymorphic (array of
 * `{name,status}` for most clients, `{name: status}` for the rest) and
 * `features/patients/utils/careGaps` owns reconciling the two.
 */
function mapCareGaps(value: unknown): RawCareGaps | null {
  if (Array.isArray(value)) return value.filter(isRecord)
  if (isRecord(value)) return value
  return null
}

/**
 * Map one relay /data/patients row to a PatientRecord.
 *
 * Relay returns clean snake_case keys (first_name, insurance, primary_insurance,
 * source_ids, phone flattened to a string, …). Exported so a contract
 * test can pin those key names — if relay ever drifts, the test fails instead of
 * this mapper silently producing ''/null (data loss with no error).
 *
 * `is_veteran` is text on the wire ('true' | 'false' | 'Veterans' | 'Chose not
 * to disclose'), not a boolean — see the field below.
 */
export function mapRelayPatient(
  patient: Record<string, unknown>
): PatientRecord {
  const firstName = patient.first_name ?? ''
  const middleName = patient.middle_name ?? ''
  const lastName = patient.last_name ?? ''

  const sourceIdsRaw = patient.source_ids

  // Phone is flattened by relay into a simple string column.
  const phone =
    typeof patient.phone === 'string' && patient.phone.trim().length > 0
      ? patient.phone.trim()
      : null

  const source_ids = isRecord(sourceIdsRaw)
    ? Object.fromEntries(
        Object.entries(sourceIdsRaw).map(([k, v]) => [k, String(v)])
      )
    : undefined

  return {
    id: String(patient.id ?? ''),
    created_at:
      typeof patient.created_at === 'string' ? patient.created_at : null,
    source_ids,
    first_name: typeof firstName === 'string' ? firstName : '',
    middle_name: typeof middleName === 'string' ? middleName : '',
    last_name: typeof lastName === 'string' ? lastName : '',
    date_of_birth:
      typeof patient.date_of_birth === 'string' ? patient.date_of_birth : null,
    gender: typeof patient.gender === 'string' ? patient.gender : null,
    race: typeof patient.race === 'string' ? patient.race : null,
    ethnicity: typeof patient.ethnicity === 'string' ? patient.ethnicity : null,
    sexual_orientation:
      typeof patient.sexual_orientation === 'string'
        ? patient.sexual_orientation
        : null,
    address: typeof patient.address === 'string' ? patient.address : null,
    zip_code: typeof patient.zip_code === 'string' ? patient.zip_code : null,
    county_name: asString(patient.county_name),
    phone,
    phones: mapPhones(patient.phones),
    email: typeof patient.email === 'string' ? patient.email : null,
    income: typeof patient.income === 'string' ? patient.income : null,
    insurance: typeof patient.insurance === 'string' ? patient.insurance : null,
    primary_insurance:
      typeof patient.primary_insurance === 'string'
        ? patient.primary_insurance
        : null,
    language: typeof patient.language === 'string' ? patient.language : null,
    uds_inc: typeof patient.uds_inc === 'string' ? patient.uds_inc : null,
    // Text on the wire, never a boolean — relay types the column `str | None`.
    // A boolean is still accepted in case the column is ever cast.
    is_veteran:
      typeof patient.is_veteran === 'boolean'
        ? String(patient.is_veteran)
        : asString(patient.is_veteran),
    housing_status:
      typeof patient.housing_status === 'string'
        ? patient.housing_status
        : null,
    is_active:
      typeof patient.is_active === 'boolean' ? patient.is_active : null,
    is_deceased:
      typeof patient.is_deceased === 'boolean' ? patient.is_deceased : null,
    // Absent key → undefined (the API does not serve care gaps), distinct from
    // an explicit null (served, never evaluated). See PatientRecord.care_gaps.
    care_gaps:
      'care_gaps' in patient ? mapCareGaps(patient.care_gaps) : undefined,
    provider_info: mapProviderInfo(patient.provider_info),
  }
}

async function fetchPatients(
  token: string,
  params: PatientsQueryParams,
  cursor?: string
): Promise<PatientsResponse> {
  const search = buildListSearchParams(params, cursor)

  const url = `${AGENT_URL}/data/patients?${search.toString()}`

  const response = await fetch(url, {
    headers: {
      // Match the calls endpoint: pass Clerk JWT directly
      Authorization: token,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Failed to fetch clients: ${response.status}`
    try {
      const errorData = JSON.parse(errorText)
      // relay validation errors surface as `detail`; hoku's own errors as `error`.
      errorMessage = errorData.detail || errorData.error || errorMessage
    } catch {
      if (errorText) errorMessage = errorText
    }
    throw new ApiError(errorMessage, response.status)
  }

  const raw = (await response.json()) as PatientsApiResponse

  return {
    data: raw.data.map(mapRelayPatient),
    pagination: parsePagination(raw.pagination, params.limit ?? 25),
  }
}

export function usePatientsQuery(
  params: PatientsQueryParams = {},
  options: { enabled?: boolean; keepPreviousData?: boolean } = {}
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useInfiniteQuery({
    queryKey: ['patients', organization?.id, params],
    queryFn: async ({ pageParam }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchPatients(token, params, pageParam as string | undefined)
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: (options.enabled ?? true) && !!organization?.id,
    // Keep prior rows visible during a filtered refetch so the table doesn't
    // flash its empty ("no results") state while the request resolves.
    placeholderData:
      options.keepPreviousData === false ? undefined : keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/** A single linked client, without retaining the previous client's identifiers. */
export function usePatientQuery(
  patientId: string | null | undefined,
  enabled = true
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  return useQuery({
    queryKey: ['patient', organization?.id, patientId],
    enabled: enabled && !!organization?.id && !!patientId,
    queryFn: async () => {
      if (!patientId) return null
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      const result = await fetchPatients(token, {
        limit: 1,
        filters: [{ id: 'id', field: 'id', operator: '=', value: patientId }],
      })
      return result.data.find((patient) => patient.id === patientId) ?? null
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
}
